import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Send, Upload, FileText, Trash2, MessageCircle } from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "proposal", "won", "lost"];

export default function LeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [docs, setDocs] = useState([]);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    const [l, m, d] = await Promise.all([
      api.get(`/leads/${id}`),
      api.get(`/whatsapp/messages/${id}`),
      api.get(`/documents?lead_id=${id}`),
    ]);
    setLead(l.data);
    setMessages(m.data);
    setDocs(d.data);
  };

  useEffect(() => { load(); }, [id]);

  const updateStatus = async (status) => {
    await api.patch(`/leads/${id}`, { status });
    toast.success("Status updated");
    load();
  };

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setSending(true);
    try {
      const r = await api.post("/whatsapp/send", { lead_id: id, message: msg });
      toast.success(r.data.mocked ? "Message sent (mock — Twilio not configured)" : "WhatsApp sent");
      setMsg("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("lead_id", id);
    try {
      await api.post("/documents/upload", fd);
      toast.success("Document uploaded");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const removeDoc = async (docId) => {
    await api.delete(`/documents/${docId}`);
    load();
  };

  if (!lead) return <div className="p-10 label-uppercase">Loading…</div>;

  return (
    <div className="min-h-screen" data-testid="lead-detail-page">
      <header className="border-b border-zinc-200 page-px py-6">
        <Link to="/leads" className="text-xs uppercase tracking-wider text-zinc-500 hover:text-[#002FA7] flex items-center gap-1 mb-3">
          <ArrowLeft size={12} /> Back to leads
        </Link>
        <div className="flex items-end justify-between">
          <div>
            <div className="label-uppercase mb-1">Lead</div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tighter" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>{lead.name}</h1>
            <div className="text-sm text-zinc-600 mt-1">{lead.company || lead.service || "—"}</div>
          </div>
          <div className="flex gap-1">
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => updateStatus(s)}
                data-testid={`status-${s}`}
                className={`text-[10px] uppercase tracking-wider px-3 py-1.5 border ${
                  lead.status === s ? "bg-ikb text-white border-[#002FA7]" : "border-zinc-300 hover:bg-zinc-50"
                }`}
              >{s}</button>
            ))}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12">
        <aside className="lg:col-span-4 lg:border-r border-b lg:border-b-0 border-zinc-200 p-6 sm:p-8 space-y-5">
          <Section label="Contact">
            <Row k="Phone" v={<span className="font-mono">{lead.phone}</span>} />
            <Row k="Email" v={lead.email || "—"} />
            <Row k="Source" v={lead.source || "manual"} />
          </Section>
          <Section label="Engagement">
            <Row k="Service" v={lead.service || "—"} />
            <Row k="Value" v={`₹${(lead.value || 0).toLocaleString()}`} />
            <Row k="Created" v={new Date(lead.created_at).toLocaleDateString()} />
          </Section>
          {lead.notes && (
            <Section label="Notes">
              <p className="text-sm leading-relaxed text-zinc-700 whitespace-pre-wrap">{lead.notes}</p>
            </Section>
          )}

          <Section label="Documents">
            <label className="block border border-dashed border-zinc-300 p-4 text-center cursor-pointer hover:bg-zinc-50">
              <input type="file" onChange={upload} className="hidden" data-testid="doc-upload-input" />
              <Upload size={16} className="mx-auto mb-1 text-zinc-500" />
              <div className="text-xs text-zinc-600">Click to upload</div>
            </label>
            <div className="mt-3 space-y-1.5">
              {docs.map((d) => (
                <div key={d.id} className="flex items-center justify-between border border-zinc-200 px-2 py-1.5 text-xs">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 truncate hover:text-[#002FA7]">
                    <FileText size={12} /> {d.filename}
                  </a>
                  <button onClick={() => removeDoc(d.id)} className="text-zinc-400 hover:text-red-600"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          </Section>
        </aside>

        <section className="lg:col-span-8 flex flex-col" data-testid="whatsapp-panel">
          <div className="border-b border-zinc-200 px-4 sm:px-8 py-4 flex items-center gap-2 bg-zinc-50">
            <MessageCircle size={16} className="text-[#25D366]" />
            <span className="font-medium tracking-tight">WhatsApp Conversation</span>
            <span className="ml-auto label-uppercase">{messages.length} messages</span>
          </div>
          <div className="flex-1 p-4 sm:p-8 space-y-3 max-h-[60vh] overflow-y-auto">
            {messages.length === 0 && (
              <div className="text-sm text-zinc-500 text-center py-12">No messages yet. Start the conversation below.</div>
            )}
            {[...messages].reverse().map((m) => (
              <div key={m.id} className="border-l-2 border-[#25D366] pl-3 py-1" data-testid={`message-${m.id}`}>
                <div className="text-sm">{m.message}</div>
                <div className="text-[10px] text-zinc-400 uppercase tracking-wider mt-1 font-mono">
                  {m.sent_by_name} · {new Date(m.created_at).toLocaleString()}
                  {m.mocked && <span className="ml-2 text-amber-600">[MOCK]</span>}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={sendMsg} className="border-t border-zinc-200 p-4 flex gap-2">
            <input
              data-testid="whatsapp-message-input"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder={`Send WhatsApp to ${lead.phone}…`}
              className="flex-1 border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-[#002FA7]"
            />
            <button
              type="submit"
              data-testid="whatsapp-send-button"
              disabled={sending}
              className="bg-[#25D366] text-white px-4 py-2 text-sm font-medium flex items-center gap-2 hover:bg-[#1eb558] disabled:opacity-50"
            >
              <Send size={14} /> Send
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="label-uppercase mb-2">{label}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex justify-between text-sm border-b border-zinc-100 py-1.5">
      <span className="text-zinc-500">{k}</span>
      <span className="text-zinc-900">{v}</span>
    </div>
  );
}
